// Phase 41.1 — typed PageContext for the chat copilot.
//
// The FE sends this on every chat message so the agent's system prompt
// knows what page the estimator is on. Too narrow → agent is dumb;
// too wide → context bloats every turn's input tokens. We enumerate the
// route discriminator explicitly so the agent can switch on it.

import { z } from 'zod';

export const PageContextRouteSchema = z.enum([
  'OPPORTUNITIES',       // /opportunities feed
  'OPPORTUNITY_DETAIL',  // /opportunities/:id
  'PROJECT_LIST',        // /projects
  'PROJECT_DETAIL',      // /projects/:id
  'PRICING',             // /pricing
  'COMPANY',             // /company / /company/dna
  'ADMIN',               // /admin/*
  'CHAT',                // /chat (full-screen) — minimal context
  'OTHER',               // unrecognized / new routes
]);
export type PageContextRoute = z.infer<typeof PageContextRouteSchema>;

// Per-project sub-tab. Surfaces in the agent's "the user is looking at..."
// block so a question like "what does this say?" can route to the right
// brain output. Optional — only sent when projectId is present.
export const ProjectTabSchema = z.enum([
  'OVERVIEW',
  'DOCUMENTS',
  'SHEETS',
  'SPECS',
  'SCHEDULES',
  'TELECOM',
  'BOM',
  'LABOR',
  'FLAGS',
  'PROPOSAL',
  'FACT_SHEET',
  'BRIEF',  // 41.6+ — chat-generated bid brief
]);
export type ProjectTab = z.infer<typeof ProjectTabSchema>;

// Innovation Audit #9 — live page snapshot.
// Compact JSON the FE publishes describing what the user is currently
// looking at. The chat agent renders this verbatim and the model
// answers from it instead of issuing redundant read tools. Capped at
// ~8KB worth of payload to keep input tokens bounded.
export const PageSnapshotSchema = z.object({
  kind: z.enum(['bom_view', 'specs_view', 'flags_view', 'opportunity_view', 'sheets_view']),
  // Free-form payload. Each `kind` defines its own shape (typed at the
  // publisher side). Server validates by size only.
  payload: z.unknown(),
});
export type PageSnapshot = z.infer<typeof PageSnapshotSchema>;

export const PageContextSchema = z.object({
  route: PageContextRouteSchema,
  // Set when route is OPPORTUNITY_DETAIL, PROJECT_DETAIL, or anything that
  // pivots on a single entity. Bare ID — the chat-agent loader tools resolve
  // full state from D1 when needed.
  projectId: z.string().max(64).nullable().optional(),
  opportunityId: z.string().max(64).nullable().optional(),
  // Per-project sub-tab when route=PROJECT_DETAIL. Drives summary defaults.
  projectTab: ProjectTabSchema.nullable().optional(),
  // Whatever rows/items the user has explicitly clicked on. Agent treats
  // these as "the user is asking about THESE specifically" rather than
  // the whole project.
  selectedEntityIds: z.array(z.string().max(64)).max(50).optional(),
  // Free-form hint the FE can pass for new routes we haven't enumerated
  // (e.g. a future /reports tab). Kept short so it doesn't bloat input.
  hint: z.string().max(256).optional(),
  // Innovation Audit #9 — currently visible data published by the host tab.
  snapshot: PageSnapshotSchema.optional(),
});
export type PageContext = z.infer<typeof PageContextSchema>;

export const EMPTY_PAGE_CONTEXT: PageContext = { route: 'OTHER' };

// Render a compact human-readable summary for the system prompt's
// "the user is on..." block. ~50-200 tokens. Keep it stable so prompt
// caching can amortize across turns with similar context.
export function renderPageContextForPrompt(ctx: PageContext): string {
  const lines: string[] = [];
  switch (ctx.route) {
    case 'OPPORTUNITIES':
      lines.push('The estimator is on the Opportunities feed.');
      break;
    case 'OPPORTUNITY_DETAIL':
      lines.push(`The estimator is viewing opportunity ${ctx.opportunityId ?? '(unknown)'}.`);
      break;
    case 'PROJECT_LIST':
      lines.push('The estimator is on the Projects list.');
      break;
    case 'PROJECT_DETAIL': {
      const tab = ctx.projectTab ? ` — ${ctx.projectTab} tab` : '';
      lines.push(`The estimator is on project ${ctx.projectId ?? '(unknown)'}${tab}.`);
      break;
    }
    case 'PRICING':
      lines.push('The estimator is on the Pricing / rate library tab.');
      break;
    case 'COMPANY':
      lines.push('The estimator is on the Company DNA / profile tab.');
      break;
    case 'ADMIN':
      lines.push('The estimator is in the admin area.');
      break;
    case 'CHAT':
      lines.push('The estimator is on the full-screen chat page (no specific project context unless an attachment or tool call provides one).');
      break;
    default:
      lines.push('The estimator is on an unrecognized page.');
  }
  if (ctx.selectedEntityIds && ctx.selectedEntityIds.length > 0) {
    lines.push(
      `Selected entities (the user likely means these specifically): ${ctx.selectedEntityIds.join(', ')}.`,
    );
  }
  if (ctx.hint) {
    lines.push(`Page hint: ${ctx.hint}`);
  }
  // Innovation Audit #9 — inline the live snapshot. Capped at 8KB after
  // JSON-stringify so we never blow input-token budgets.
  if (ctx.snapshot) {
    try {
      const json = JSON.stringify(ctx.snapshot.payload);
      const truncated = json.length > 8192 ? json.slice(0, 8192) + '…(truncated)' : json;
      lines.push('');
      lines.push(`## Currently visible data (${ctx.snapshot.kind})`);
      lines.push(
        'The user can see this data on screen right now. ANSWER FROM THIS DIRECTLY when the question is answerable from it — do NOT call a read_* tool just to re-fetch what is here. Only call a tool when the snapshot is missing or insufficient.',
      );
      lines.push('```json');
      lines.push(truncated);
      lines.push('```');
    } catch {
      // Snapshot payload not serializable — skip.
    }
  }
  return lines.join('\n');
}
